// On-page project bar — sits at the top of File Upload and AI Insights so the active
// project is visible RIGHT where the content is, and switchable inline. The Company
// layer is hidden from the UI (R32): this shows a flat project list, with a per-PROJECT
// color chip for identity.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check, Plus, Search } from "lucide-react";
import { useProjects } from "../ProjectContext";
import CompanyAvatar from "./CompanyAvatar";
import NewProjectModal from "./NewProjectModal";

export default function ProjectBar({ inline = false }) {
  const { projects, activeProject, activeProjectId, selectProject, loading, projectSwitchBlocked } = useProjects();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  // Flat, recency-ordered project list, type-to-filter when it grows.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => !q || p.displayName.toLowerCase().includes(q));
  }, [projects, query]);

  // If a batch starts while the dropdown is open, close it — otherwise the list stays clickable
  // and each click would be silently refused by selectProject's guard.
  useEffect(() => { if (projectSwitchBlocked) setOpen(false); }, [projectSwitchBlocked]);

  // selectProject returns false if it refused (upload in flight). Only collapse the picker when
  // the switch actually happened, so a refusal can never look like a successful switch.
  const pick = (pid) => { if (selectProject(pid) !== false) { setOpen(false); setQuery(""); } };

  return (
    <div ref={rootRef} style={{ position: "relative", marginBottom: inline ? 0 : "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 600 }}>Working in</span>
        {/* Disabled while an upload batch is importing: that batch is pinned to the project it
            started in, so switching now would show one project while files land in another.
            Disabling (instead of letting the click be refused) keeps it from silently no-opping. */}
        <button onClick={() => { if (!projectSwitchBlocked) setOpen((o) => !o); }}
          disabled={projectSwitchBlocked}
          title={projectSwitchBlocked
            ? `Finishing upload into "${activeProject?.displayName || "this project"}" — you can switch projects once it completes.`
            : "Switch project"}
          style={{
          display: "inline-flex", alignItems: "center", gap: "9px", padding: "7px 12px", borderRadius: "10px",
          border: "1px solid #e2e8f0", background: projectSwitchBlocked ? "#f8fafc" : "#fff",
          cursor: projectSwitchBlocked ? "not-allowed" : "pointer",
          opacity: projectSwitchBlocked ? 0.65 : 1, maxWidth: "360px",
        }}>
          {/* Color chip keyed on the PROJECT id (company hidden — R32) */}
          <CompanyAvatar companyId={activeProject?.projectId || "default"} name={activeProject?.displayName || "My Data"} size={20} />
          <span style={{ fontSize: "14px", color: "#0f172a", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "300px" }}>
            {activeProject?.displayName || (loading ? "Loading…" : "My Data")}
          </span>
          <ChevronDown size={16} style={{ color: "#94a3b8", flexShrink: 0 }} />
        </button>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: "0", zIndex: 9000, width: "340px",
          maxHeight: "60vh", overflow: "hidden", display: "flex", flexDirection: "column",
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px", boxShadow: "0 16px 48px rgba(15,23,42,0.18)",
        }}>
          {projects.length > 6 && (
            <div style={{ padding: "10px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "9px", padding: "6px 9px" }}>
                <Search size={14} style={{ color: "#94a3b8" }} />
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search projects"
                  style={{ border: "none", outline: "none", background: "transparent", fontSize: "13px", width: "100%", color: "#0f172a" }} />
              </div>
            </div>
          )}
          <div style={{ overflowY: "auto", padding: "6px" }}>
            {filtered.length === 0 && <div style={{ padding: "16px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>{loading ? "Loading…" : "No projects"}</div>}
            {filtered.map((p) => {
              const active = p.projectId === activeProjectId;
              return (
                <div key={p.projectId} onClick={() => pick(p.projectId)}
                  style={{ display: "flex", alignItems: "center", gap: "9px", padding: "9px 10px", borderRadius: "9px", cursor: "pointer", background: active ? "#eef2ff" : "transparent" }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                  <CompanyAvatar companyId={p.projectId} name={p.displayName} size={18} />
                  <span style={{ flex: 1, fontSize: "13.5px", fontWeight: active ? 700 : 500, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.displayName}</span>
                  {active && <Check size={15} style={{ color: "#4f46e5", flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
          <div style={{ borderTop: "1px solid #f1f5f9", padding: "8px" }}>
            <button onClick={() => { setOpen(false); setNewOpen(true); }} style={{
              width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
              padding: "9px", borderRadius: "9px", border: "1px dashed #c7d2fe", background: "#f5f3ff", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer",
            }}><Plus size={15} /> New Project</button>
          </div>
        </div>
      )}

      <NewProjectModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}
